import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { accountsApi } from '../api/accounts-api';
import type { IAdminAccountDetail, IAdminAccountRow } from '../api/account-contracts';
import type { IPermissionInfo, IRole } from '../api/role-contracts';
import { rolesApi } from '../api/roles-api';
import { NOT_REREAD, unanswered } from '../pages/person-hub/said';
import { TEAM_PATHS } from '../paths';
import {
  ROLES_FORBIDDEN,
  namesSomethingGone,
  neverArrived,
  refusalMessage,
  roleModelRefusal,
  statusRefusal
} from '../refusal';
import { absent } from './account-facts';
import { LoadError, useAdminData, type IAdminTab } from './admin-page';

/**
 * An account's Team Portal roles (SR-228, the role-model UI design's §6) — the write that replaced
 * the one role grant, drawn on the user card and, for a viewer holding `accounts.browse`, beside
 * the Sign-in card on the person hub. **An account's kind is not changed here, or anywhere**
 * (SR-411, Veritant's ruling — "a hazard to have"): the portal an account signs in to is fixed when
 * it is made, and a Team Portal seat is made as one on Create an account.
 *
 * It is `roles.manage` (`canGrant`, from `accountPowers`), judged by survey-api — which roles
 * the caller may give (D5), whether a change leaves nobody able to manage roles — and says its
 * refusal beside the control (`roleModelRefusal`), never predicted here. Every write that lands
 * re-reads the page, the user card's rule — and so does one whose answer never arrived, or that
 * met a 404.
 */

/**
 * The role list, read only where it can be — `GET /admin/roles` is `roles.manage`, and a reader
 * without it is asked nothing it would be refused. Re-read per mount: roles are edited on Setup
 * without a release.
 */
export function useRoleList(readable: boolean) {
  return useAdminData<{ roles: Nullable<IRole[]> }>(
    async (signal) => {
      if (!readable) return { roles: null };
      try {
        return { roles: await rolesApi.listRoles(signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the Team Portal roles'));
      }
    },
    [readable]
  );
}

/** The permission catalogue, on the same terms as the role list. */
export function usePermissionCatalogue(readable: boolean) {
  return useAdminData<{ permissions: Nullable<IPermissionInfo[]> }>(
    async (signal) => {
      if (!readable) return { permissions: null };
      try {
        return { permissions: await rolesApi.permissions(signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the permission catalogue'));
      }
    },
    [readable]
  );
}

/** One group of the permission catalogue, as survey-api orders and words it. */
export interface IPermissionGroup {
  group: string;
  label: string;
  entries: IPermissionInfo[];
}

/**
 * The catalogue's permissions in its groups — the catalogue's order, a group labelled by its code,
 * since survey-api serves no label for one and this side holds no group words — keeping only those
 * `keep` takes. One grouping for "What this account can do" and a role's permissions.
 */
export function groupCatalogue(
  catalogue: readonly IPermissionInfo[],
  keep: (code: string) => boolean = () => true
): IPermissionGroup[] {
  const groups = new Map<string, IPermissionGroup>();
  for (const entry of catalogue) {
    if (!keep(entry.code)) continue;
    const group = groups.get(entry.group) ?? {
      group: entry.group,
      label: entry.group,
      entries: []
    };
    group.entries.push(entry);
    groups.set(entry.group, group);
  }
  return [...groups.values()];
}

/**
 * "Users & roles"' routed tabs — Users `accounts.browse`, Roles `roles.manage` — those the account
 * holds, each a section of its own (the role-model UI design's §4). None where it holds one only:
 * a strip of one tab names nothing to switch to.
 */
export function useUsersAndRolesTabs(): IAdminTab[] | undefined {
  const can = useCan();
  const tabs = [
    ...(can('accounts.browse') ? [{ to: TEAM_PATHS.users, label: 'Users' }] : []),
    ...(can('roles.manage') ? [{ to: TEAM_PATHS.roles, label: 'Roles' }] : [])
  ];
  return tabs.length > 1 ? tabs : undefined;
}

/** What a write was told, beside its control — the landing, or survey-api's refusal. */
interface ISaid {
  tone: 'saved' | 'refused';
  text: string;
}

/** A write's answer as one paragraph: a refusal loud (`alert`), a landing quiet (`status`). */
function SaidLine({ said }: { said: Nullable<ISaid> }) {
  if (!said) return null;
  return (
    <p
      role={said.tone === 'refused' ? 'alert' : 'status'}
      className={
        said.tone === 'refused' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
      }
    >
      {said.text}
    </p>
  );
}

/** Why a write on the caller's own account is not offered — survey-api's `own-account`, said
 *  before the press rather than after it. `change` names what would change. */
function OwnAccountNotice({ id, change }: { id: string; change: string }) {
  return (
    <Alert>
      <AlertTitle>This is your own account</AlertTitle>
      <AlertDescription id={id}>
        The system refuses a change to {change} of the account making it. Ask another
        administrator.
      </AlertDescription>
    </Alert>
  );
}

/** One role as a box, by its name with its description — the roles card's and a new seat's. */
export function RoleCheckbox({
  id,
  name,
  description,
  checked,
  disabled,
  onCheckedChange
}: {
  id: string;
  name: string;
  description?: Nullable<string>;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (on: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
      <label htmlFor={id} className="grid gap-0.5">
        <span className="font-medium">{name}</span>
        {description && <span className="text-muted-foreground">{description}</span>}
      </label>
    </div>
  );
}

/** The refusals neither write's problem type names. 403 is bodiless here: the policy refusing a
 *  caller whose roles no longer include `roles.manage`. A 404 is the account gone, read again. */
const ACCOUNT_WRITE_REFUSALS: Record<number, string> = {
  403: ROLES_FORBIDDEN,
  404: 'This account is no longer registered — the page is read again.'
};

/**
 * A roles or kind write's refusal, and whether the card reads the account again: an answer that
 * never arrived may have landed, and a 404 says the page is out of date — both re-read. `what`
 * completes "The answer to …".
 */
function accountWriteRefusal(
  error: unknown,
  what: string,
  fallback: string
): { said: ISaid; reread: boolean } {
  if (unanswered(error)) {
    return {
      said: {
        tone: 'refused',
        text: neverArrived(what, 'The account is read again — check it before trying again.')
      },
      reread: true
    };
  }
  return {
    said: {
      tone: 'refused',
      text: roleModelRefusal(error) ?? statusRefusal(error, ACCOUNT_WRITE_REFUSALS, fallback)
    },
    reread: error instanceof ApiError && error.status === 404
  };
}

/**
 * `PUT …/accounts/{accountId}/roles` — every role a checkbox, by name with its description, and
 * the whole set sent on Save; an unchanged set sends nothing (survey-api's no-op). Drawn on a
 * Team Portal account; on an advisor the card says roles are a Team Portal account's.
 *
 * A role the account holds that the list does not carry (read a moment apart) is still drawn,
 * by its name, so Save never drops it unseen. A role holding a permission the caller lacks is
 * offered all the same: survey-api's `permission-not-held` is the judgement (D5), said here.
 *
 * A landing holds the set it sent as the stored one (`landed`) until a read carries it, so a
 * re-read that fails offers no Save beside "Saved." — it says the page could not be read again.
 */
export function AccountRolesCard({
  account,
  isSelf,
  canGrant,
  onChanged
}: {
  account: IAdminAccountRow;
  isSelf: boolean;
  canGrant: boolean;
  /** The page's re-read — `false` where it failed. */
  onChanged: () => Promise<boolean>;
}) {
  const held = React.useMemo(() => account.roles.map((role) => role.roleId), [account.roles]);
  const heldKey = held.join(',');
  const writable = account.kind === 'staff' && canGrant && !isSelf;
  const { data, error, reload } = useRoleList(writable);
  const [draft, setDraft] = React.useState<readonly string[]>(held);
  const [landed, setLanded] = React.useState<Nullable<readonly string[]>>(null);
  const [saving, setSaving] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<ISaid>>(null);
  const reasonId = React.useId();

  // A re-read after a write — or one the page made for any other reason — is the new baseline.
  // Keyed on the ids' spelling, not the array: every re-read hands over a new array.
  React.useEffect(() => {
    setDraft(held);
    setLanded(null);
  }, [heldKey]);

  if (account.kind !== 'staff') {
    return (
      <Card data-account-roles>
        <CardHeader>
          <CardTitle>Team Portal roles</CardTitle>
          <CardDescription>
            Roles are a Team Portal account’s. This is an Advisor Portal account, which signs in to
            fill surveys and holds none.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const listed = data?.roles ?? null;
  // Every role the list carries, then any role held that it does not — nothing held is dropped.
  const offered: { roleId: string; name: string; description: Nullable<string> }[] = [
    ...(listed ?? []),
    ...account.roles
      .filter((role) => !listed?.some((candidate) => candidate.roleId === role.roleId))
      .map((role) => ({ roleId: role.roleId, name: role.name, description: null }))
  ];
  const stored = landed ?? held;
  const changed =
    draft.length !== stored.length || draft.some((roleId) => !stored.includes(roleId));

  const toggle = (roleId: string, on: boolean) => {
    setSaid(null);
    setDraft((current) =>
      on ? [...current, roleId] : current.filter((candidate) => candidate !== roleId)
    );
  };

  const save = async () => {
    setSaving(true);
    setSaid(null);
    try {
      await accountsApi.setRoles(account.accountId, draft);
      setLanded(draft);
      const reread = await onChanged();
      setSaid({
        tone: 'saved',
        text:
          'Saved. Every session the account held was signed out, so its holder signs in again.' +
          (reread ? '' : NOT_REREAD)
      });
    } catch (saveError) {
      const refusal = accountWriteRefusal(
        saveError,
        'saving the roles',
        'Could not save the roles'
      );
      setSaid(refusal.said);
      if (namesSomethingGone(saveError)) void reload();
      if (refusal.reread) await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const shown = writable ? offered : account.roles;

  return (
    <Card data-account-roles>
      <CardHeader>
        <CardTitle>Team Portal roles</CardTitle>
        <CardDescription>
          What this account can open and do in the Team Portal is the sum of the roles it holds.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {writable && error && listed === null && (
          <div className="grid justify-items-start gap-2">
            <LoadError message={error} />
            <Button variant="outline" size="sm" onClick={() => void reload()}>
              Read again
            </Button>
          </div>
        )}
        {writable && data === null && !error ? (
          <p className="text-sm text-muted-foreground">Reading the roles…</p>
        ) : (
          <fieldset className="grid gap-3" aria-describedby={writable ? undefined : reasonId}>
            <legend className="sr-only">Team Portal roles of {account.email}</legend>
            {shown.length === 0 ? (
              <p className="text-sm">{absent('No Team Portal role yet.')}</p>
            ) : (
              shown.map((role) => (
                <RoleCheckbox
                  key={role.roleId}
                  id={`${reasonId}-${role.roleId}`}
                  name={role.name}
                  description={'description' in role ? role.description : null}
                  checked={writable ? draft.includes(role.roleId) : true}
                  disabled={!writable || saving}
                  onCheckedChange={(on) => toggle(role.roleId, on)}
                />
              ))
            )}
          </fieldset>
        )}

        {isSelf ? (
          <OwnAccountNotice id={reasonId} change="the roles" />
        ) : (
          !canGrant && <NotPermitted id={reasonId} what="changing an account’s roles" />
        )}

        {writable && changed && (
          <Alert>
            <AlertTitle>This signs the account out</AlertTitle>
            <AlertDescription>
              Every session it holds is revoked when its roles change, so its holder signs in
              again before doing anything.
            </AlertDescription>
          </Alert>
        )}

        {writable && (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void save()} disabled={!changed || saving || listed === null}>
              {saving ? 'Saving…' : 'Save roles'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDraft(stored);
                setSaid(null);
              }}
              disabled={!changed || saving}
            >
              Cancel
            </Button>
          </div>
        )}

        <SaidLine said={said} />
      </CardContent>
    </Card>
  );
}
